import { useNavigate } from "react-router-dom";
import { Layout, Button, SectionLabel, SectionHeading, PageHero } from "../components.jsx";
import { navy, blue } from "../data";

const VALUES = [
  { icon:"🎯", title:"Practical Over Theoretical",    desc:"We only recommend AI that can be implemented, used, and measured. No vanity projects, no overpromised outcomes." },
  { icon:"🏗️", title:"Build for Your Business",       desc:"Every system we design is built around your actual workflows — not adapted from a generic template." },
  { icon:"📊", title:"Results You Can Measure",       desc:"We define success upfront: time saved, leads captured, reports automated. You should always know what you're getting." },
  { icon:"🤝", title:"A Partner, Not a Vendor",       desc:"We work directly with founders and operations teams — not through account managers or project layers." },
];

export default function About() {
  const navigate = useNavigate();

  return (
    <Layout>
      <PageHero
        label="About VedaLogic"
        title="AI Consulting Grounded in Business Reality"
        subtitle="We believe AI should work for your business — not the other way around. Practical systems. Measurable outcomes. No hype."
      />

      {/* ── STORY ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <SectionLabel>Our Story</SectionLabel>
              <SectionHeading>Why VedaLogic Was Founded</SectionHeading>
              <p className="text-gray-500 mt-5 mb-5 leading-relaxed text-base">
                VedaLogic was founded on the belief that AI should work for your business — not the other way around. We focus on practical, deployable systems that reduce manual work, improve visibility, and create measurable operational results.
              </p>
              <p className="text-gray-500 mb-5 leading-relaxed text-base">
                Too many businesses are sold complex tools they don't need, or left with implementations that never get used. The consulting market is full of strategy decks with no delivery, and technology vendors with no business context.
              </p>
              <p className="text-gray-500 leading-relaxed text-base">
                Our approach is different. We start with your operations, understand your real problems, and build AI systems that fit the way your team actually works — practical, deployable, and built to last.
              </p>
            </div>
            <div className="rounded-lg p-10 border border-gray-100" style={{ background: "#f8faff" }}>
              <div className="w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-6" style={{ background: navy }}>P</div>
              <h3 className="font-bold text-xl mb-1" style={{ color: navy }}>Pankaj</h3>
              <p className="font-semibold text-sm mb-5" style={{ color: blue }}>Founder, VedaLogic</p>
              <div className="space-y-3">
                {[
                  "Business-focused AI consulting and implementation",
                  "Specialist in operational automation for service businesses",
                  "Focused on practical, results-driven AI deployment",
                  "Works directly with founders and operations teams",
                ].map(item => (
                  <p key={item} className="text-gray-600 text-sm flex items-start gap-2">
                    <span className="flex-shrink-0" style={{ color: blue }}>▸</span>{item}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR VALUES ──────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>How We Work</SectionLabel>
            <SectionHeading>Our Approach to AI Consulting</SectionHeading>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map(v => (
              <div key={v.title} className="bg-white rounded-lg p-7 border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-200">
                <div className="text-3xl mb-4">{v.icon}</div>
                <h3 className="font-bold text-base mb-2" style={{ color: navy }}>{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY SERVICE BUSINESSES ──────────────────────────────────────────── */}
      <section className="py-24" style={{ background: navy }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionLabel>Our Focus</SectionLabel>
          <SectionHeading light>Why We Focus on Service Businesses</SectionHeading>
          <p className="text-gray-400 mt-5 mb-5 leading-relaxed text-base max-w-2xl mx-auto">
            We work with a focused set of industries — landscaping, home services, professional services, real estate, construction, healthcare, logistics, property management, and e-commerce.
          </p>
          <p className="text-gray-400 leading-relaxed text-base max-w-2xl mx-auto">
            The reason is simple: by going deep in a defined set of sectors, we understand your terminology, your customer dynamics, your seasonal pressures, and your operational reality. That focus means we build better systems faster — and our clients get better results.
          </p>
        </div>
      </section>

      {/* ── PROOF WE BUILD ──────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Proof We Build</SectionLabel>
            <SectionHeading>We don't just advise — we build systems that run real operations</SectionHeading>
          </div>
          <div className="rounded-lg overflow-hidden border border-gray-100 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-10" style={{ background: navy }}>
                <p className="text-blue-300 text-xs font-bold uppercase tracking-widest mb-3">Case Study</p>
                <h3 className="text-white font-bold text-2xl mb-4">Dancerise</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Most service businesses run on a mix of texts, spreadsheets, and manual coordination. That works — until it doesn't scale.
                </p>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                  Dancerise was built to solve that exact problem for dance studios: fragmented scheduling, scattered communication, and no clear system of record. It replaces manual coordination with a structured platform where operations run consistently without constant owner involvement.
                </p>
                <a
                  href="https://dance-rise.replit.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm font-semibold transition-colors"
                >
                  See the live system →
                </a>
              </div>
              <div className="p-10 bg-gray-50">
                <p className="text-blue-600 text-xs font-bold uppercase tracking-widest mb-6">What This Proves</p>
                <div className="space-y-5">
                  {[
                    "We don't design workflows in theory — we build systems that handle real, day-to-day operations",
                    "We understand how messy service businesses actually run — and how to bring structure without overcomplicating",
                    "We can take an operational problem from idea to live product — not just recommend tools",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: navy }}>
                        <span className="text-white text-xs font-bold">{i + 1}</span>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionLabel>Work With Us</SectionLabel>
          <SectionHeading>Ready to Work With Pankaj?</SectionHeading>
          <p className="text-gray-500 mt-4 mb-10 text-base max-w-xl mx-auto leading-relaxed">
            Whether you're exploring AI for the first time or ready to start building, get in touch and we'll have an honest conversation about what's possible in your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate("/contact")}>Get In Touch</Button>
            <Button variant="outline" onClick={() => navigate("/ai-assessment")}>Start With an Assessment</Button>
          </div>
        </div>
      </section>

    </Layout>
  );
}